import { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { useParagraphGenerations } from '../../api/queries/generations';
import { AppImage } from '../common/AppImage';
import { AudioPlayer } from '../common/AudioPlayer';
import { ImageLightbox } from '../common/ImageLightbox';

type FilterType = 'all' | 'image' | 'audio' | 'text';

interface Props {
  paragraphId: string | null;
}

function classifyGeneration(slug: string): 'image' | 'audio' | 'text' {
  const s = slug.toLowerCase();
  if (s.includes('image')) return 'image';
  if (s.includes('audio') || s.includes('narrat') || s.includes('voice'))
    return 'audio';
  return 'text';
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function statusColor(
  status: string,
): 'success' | 'error' | 'warning' | 'default' {
  switch (status) {
    case 'complete':
      return 'success';
    case 'failed':
      return 'error';
    case 'processing':
      return 'warning';
    default:
      return 'default';
  }
}

function TypeIcon({ type }: { type: 'image' | 'audio' | 'text' }) {
  switch (type) {
    case 'image':
      return <ImageIcon fontSize="small" color="action" />;
    case 'audio':
      return <AudiotrackIcon fontSize="small" color="action" />;
    case 'text':
      return <TextFieldsIcon fontSize="small" color="action" />;
  }
}

/**
 * Lists historical AI generations for the selected paragraph as enhanced cards
 * with filter chips, image thumbnails, audio playback, and text previews.
 * Re-queries every 5s while any row is still processing.
 */
export function GenerationHistoryPanel({ paragraphId }: Props) {
  const { data, isLoading } = useParagraphGenerations(paragraphId);
  const [filter, setFilter] = useState<FilterType>('all');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (!paragraphId) return null;

  const generations = data ?? [];
  const filtered =
    filter === 'all'
      ? generations
      : generations.filter((row) => classifyGeneration(row.slug) === filter);

  // Collect image URLs for the lightbox
  const imageEntries = generations
    .filter((row) => {
      const r = row.result as { url?: string; image_id?: string } | null;
      return r?.url && r?.image_id;
    })
    .map((row) => {
      const r = row.result as { url: string };
      return { src: r.url, alt: row.slug, label: row.slug };
    });

  const filters: { label: string; value: FilterType }[] = [
    { label: 'All', value: 'all' },
    { label: 'Images', value: 'image' },
    { label: 'Audio', value: 'audio' },
    { label: 'Text', value: 'text' },
  ];

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Generation history
      </Typography>

      {/* Filter chips */}
      <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
        {filters.map((f) => (
          <Chip
            key={f.value}
            label={f.label}
            size="small"
            variant={filter === f.value ? 'filled' : 'outlined'}
            color={filter === f.value ? 'primary' : 'default'}
            onClick={() => setFilter(f.value)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Stack>

      {isLoading && <Typography variant="caption">Loading...</Typography>}

      {!isLoading && filtered.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', py: 2, textAlign: 'center' }}
        >
          {generations.length === 0
            ? 'No generations yet for this paragraph. Use the toolbar or right-click to generate.'
            : 'No generations match this filter.'}
        </Typography>
      )}

      <Stack spacing={1}>
        {filtered.map((row) => {
          const genType = classifyGeneration(row.slug);
          return (
            <GenerationCard
              key={row.jobId}
              slug={row.slug}
              status={row.status}
              costCredits={row.costCredits}
              createdAt={row.createdAt}
              errorMessage={row.errorMessage}
              result={row.result}
              genType={genType}
              onImageClick={() => {
                // Find matching index in imageEntries
                const r = row.result as {
                  url?: string;
                  image_id?: string;
                } | null;
                if (r?.url) {
                  const idx = imageEntries.findIndex((e) => e.src === r.url);
                  if (idx >= 0) setLightboxIndex(idx);
                }
              }}
            />
          );
        })}
      </Stack>

      {/* Lightbox for image previews */}
      {lightboxIndex !== null && (
        <ImageLightbox
          open
          images={imageEntries}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </Paper>
  );
}

interface GenerationCardProps {
  slug: string;
  status: string;
  costCredits: number;
  createdAt: string;
  errorMessage?: string | null;
  result?: unknown;
  genType: 'image' | 'audio' | 'text';
  onImageClick: () => void;
}

function GenerationCard({
  slug,
  status,
  costCredits,
  createdAt,
  errorMessage,
  result,
  genType,
  onImageClick,
}: GenerationCardProps) {
  return (
    <Card variant="outlined" sx={{ overflow: 'visible' }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {/* Header row: status chip, type icon, slug, timestamp, cost */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip size="small" color={statusColor(status)} label={status} />
          <TypeIcon type={genType} />
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {slug}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {costCredits}c
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {relativeTime(createdAt)}
          </Typography>
        </Stack>

        {/* Result preview */}
        <GenerationResultPreview
          result={result}
          genType={genType}
          onImageClick={onImageClick}
        />

        {/* Error message */}
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 1, py: 0 }} variant="outlined">
            <Typography variant="caption">{errorMessage}</Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function GenerationResultPreview({
  result,
  genType,
  onImageClick,
}: {
  result: unknown;
  genType: 'image' | 'audio' | 'text';
  onImageClick: () => void;
}) {
  const r = result as {
    url?: string;
    image_id?: string;
    audio_id?: string;
    text?: string;
    generated_text?: string;
  } | null;

  if (!r) return null;

  // Image result
  if (r.url && r.image_id) {
    return (
      <Box sx={{ mt: 1 }}>
        <AppImage
          src={r.url}
          alt="Generated image"
          width={120}
          height={80}
          objectFit="cover"
          objectPosition="top center"
          onClick={onImageClick}
          sx={{ borderRadius: 1, cursor: 'pointer' }}
        />
      </Box>
    );
  }

  // Audio result
  if (r.url && r.audio_id) {
    return (
      <Box sx={{ mt: 1 }}>
        <AudioPlayer src={r.url} compact />
      </Box>
    );
  }

  // Text result
  if (genType === 'text') {
    const textContent = r.text ?? r.generated_text;
    if (textContent) {
      const preview =
        textContent.length > 100
          ? textContent.slice(0, 100) + '...'
          : textContent;
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: 1,
            display: 'block',
            fontStyle: 'italic',
            lineHeight: 1.4,
          }}
        >
          {preview}
        </Typography>
      );
    }
  }

  return null;
}
